import * as Sentry from '@sentry/react';

/**
 * Detecta se o usuário está no Instagram WebView (navegador interno do app)
 */
export function isInstagramWebView(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  return /instagram|fban|fbav/.test(ua);
}

/**
 * Detecta se o usuário está no Facebook WebView
 */
export function isFacebookWebView(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  return /fban|fbav|facebook/.test(ua) && !/instagram/.test(ua);
}

/**
 * Detecta se está em qualquer navegador interno (Instagram, Facebook, etc)
 */
export function isInAppBrowser(): boolean {
  return isInstagramWebView() || isFacebookWebView();
}

/**
 * Tipos de eventos de observabilidade
 */
export type ObservabilityEvent =
  | 'user_login'
  | 'user_logout'
  | 'list_created'
  | 'list_viewed'
  | 'list_shared'
  | 'item_added'
  | 'item_removed'
  | 'item_reserved'
  | 'checkout_started'
  | 'checkout_completed'
  | 'page_error'
  | 'api_error'
  | 'navigation';

export interface ObservabilityContext {
  userEmail?: string;
  listId?: string;
  listTitle?: string;
  itemId?: string;
  itemTitle?: string;
  browser?: string;
  isInstagram?: boolean;
  isFacebook?: boolean;
  [key: string]: unknown;
}

type MetricAttributeValue = string | number | boolean;

const MAX_METRIC_ATTRIBUTES = 40;
const MAX_STRING_VALUE = 180;

function normalizeMetricName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_.-]/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_+|_+$/g, '');
}

function toMetricAttributeValue(value: unknown): MetricAttributeValue | undefined {
  if (typeof value === 'string') {
    return value.slice(0, MAX_STRING_VALUE);
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (value instanceof Date) {
    return value.toISOString().slice(0, MAX_STRING_VALUE);
  }
  if (value === null || value === undefined) {
    return undefined;
  }

  try {
    return JSON.stringify(value).slice(0, MAX_STRING_VALUE);
  } catch {
    return String(value).slice(0, MAX_STRING_VALUE);
  }
}

function toMetricAttributes(context: ObservabilityContext): Record<string, MetricAttributeValue> {
  const attributes: Record<string, MetricAttributeValue> = {};
  const entries = Object.entries(context).slice(0, MAX_METRIC_ATTRIBUTES);

  for (const [rawKey, rawValue] of entries) {
    const key = normalizeMetricName(rawKey);
    if (!key) continue;
    const value = toMetricAttributeValue(rawValue);
    if (value !== undefined) {
      attributes[key] = value;
    }
  }

  return attributes;
}

function enrichContext(context: ObservabilityContext = {}): ObservabilityContext {
  const browser =
    context.browser ||
    (isInstagramWebView() ? 'instagram' : isFacebookWebView() ? 'facebook' : 'browser');
  const isInstagram = isInstagramWebView();
  const isFacebook = isFacebookWebView();

  return {
    ...context,
    browser,
    isInstagram,
    isFacebook,
    site: process.env.REACT_APP_SITE_NAME || 'unknown',
    environment: process.env.NODE_ENV || 'development',
    inAppBrowser: isInstagram || isFacebook,
    timestamp: new Date().toISOString(),
  };
}

export function trackMetricCount(metricName: string, context: ObservabilityContext = {}): void {
  if (typeof window === 'undefined') return;

  const enrichedContext = enrichContext(context);
  const metric = normalizeMetricName(metricName);
  if (!metric) return;

  Sentry.metrics.count(metric, 1, {
    attributes: toMetricAttributes(enrichedContext),
  });
}

export function trackMetricDuration(
  metricName: string,
  durationMs: number,
  context: ObservabilityContext = {}
): void {
  if (typeof window === 'undefined' || !Number.isFinite(durationMs)) return;

  const enrichedContext = enrichContext(context);
  const metric = normalizeMetricName(metricName);
  if (!metric) return;

  Sentry.metrics.distribution(metric, durationMs, {
    unit: 'millisecond',
    attributes: toMetricAttributes(enrichedContext),
  });
}

export function trackLogInfo(message: string, context: ObservabilityContext = {}): void {
  if (typeof window === 'undefined') return;
  const enrichedContext = enrichContext(context);
  Sentry.logger.info(message, enrichedContext);
}

export function trackLogWarn(message: string, context: ObservabilityContext = {}): void {
  if (typeof window === 'undefined') return;
  const enrichedContext = enrichContext(context);
  Sentry.logger.warn(message, enrichedContext);
}

/**
 * Registra eventos estruturados para observabilidade
 * Envia para Sentry com contexto de Instagram/Facebook
 */
export function recordEvent(
  eventName: ObservabilityEvent,
  context: ObservabilityContext = {}
): void {
  const enrichedContext = enrichContext({
    ...context,
    eventType: eventName,
  });

  trackMetricCount(`funnel.${eventName}`, enrichedContext);

  // Log estruturado no console (dev)
  if (process.env.NODE_ENV === 'development') {
    console.log(`[${eventName}]`, enrichedContext);
  }
}

/**
 * Registra erros com contexto de Instagram/Facebook
 */
export function recordError(error: Error | string, context: ObservabilityContext = {}): void {
  if (typeof window === 'undefined') return;

  const enrichedContext = enrichContext(context);

  const err = typeof error === 'string' ? new Error(error) : error;

  trackMetricCount('funnel.error', {
    ...enrichedContext,
    errorName: err.name,
  });

  Sentry.logger.error('flow.error', {
    ...enrichedContext,
    errorName: err.name,
    errorMessage: err.message,
  });

  Sentry.captureException(err, {
    tags: {
      browser: String(enrichedContext.browser || 'browser'),
      in_app_browser: enrichedContext.inAppBrowser ? 'true' : 'false',
    },
    extra: enrichedContext,
  });
}

/**
 * Registra tempo de operação (performance)
 * Útil para entender se há lentidão no Instagram vs browser normal
 */
export function recordTiming(
  operationName: string,
  duration: number,
  context: ObservabilityContext = {}
): void {
  const enrichedContext = enrichContext({
    ...context,
    operation: operationName,
  });

  trackMetricDuration(`perf.${operationName}`, duration, enrichedContext);

  if (process.env.NODE_ENV === 'development') {
    console.log(`[TIMING] ${operationName}: ${duration}ms (${enrichedContext.browser})`);
  }
}

/**
 * Hook para medir performance de uma função async
 */
export async function withTiming<T>(
  operationName: string,
  fn: () => Promise<T>,
  context?: ObservabilityContext
): Promise<T> {
  const startTime = performance.now();
  try {
    const result = await fn();
    const duration = Math.round(performance.now() - startTime);
    recordTiming(operationName, duration, context);
    return result;
  } catch (error) {
    const duration = Math.round(performance.now() - startTime);
    recordTiming(`${operationName} (ERROR)`, duration, context);
    throw error;
  }
}
